import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import apiInterceptor from '@/shared/apiInterceptor';
import { environment } from '@/shared/env';
import { queryClient } from '@/shared/queryClient';

export const customerTagsQueryOptions = {
  queryKey: ['customer-tags'],
  queryFn: async () => {
    const r = await apiInterceptor.get(environment('VUE_APP_API_URL') + 'crm/v1/customers/tags');
    const tags = r?.tags ?? r?.data?.tags;
    return Array.isArray(tags) ? tags : [];
  },
  staleTime: Infinity,
};

export function useCustomerTags() {
  const query = useQuery(customerTagsQueryOptions);
  
  // Create a reactive ref that syncs with cache
  const cachedTags = ref([]);
  
  // Initialize from cache
  const initializeFromCache = () => {
    cachedTags.value = queryClient.getQueryData(['customer-tags']) ?? [];
  };
  
  // Subscribe to cache changes to keep cachedTags in sync
  const unsubscribe = queryClient.getQueryCache().subscribe(() => {
    initializeFromCache();
  });
  
  // Initialize on first use
  initializeFromCache();

  return {
    // Use cachedTags as the reactive source, with fallback to query data
    tags: computed(() => cachedTags.value.length > 0 ? cachedTags.value : query.data.value ?? []),
    tagsLoading: query.isLoading,
    tagsFetching: query.isFetching,
    loadTags: query.refetch,
    unsubscribe, // Return unsubscribe so components can cleanup
  };
}

/**
 * Optimistically adds a new tag to the cache immediately for instant UI feedback.
 * The tag is added to the existing cached list without refetching.
 * Use this for immediate UX response when a new tag is created.
 */
export function optimisticallyAddTag(newTag) {
  const queryKey = ['customer-tags'];
  const currentData = queryClient.getQueryData(queryKey) ?? [];
  
  // Only add if it doesn't already exist
  if (!currentData.includes(newTag)) {
    const updatedData = [...currentData, newTag];
    queryClient.setQueryData(queryKey, updatedData);
  }
}

/**
 * Clears the customer tags cache and refetches fresh data from the API.
 * Call this after tags are created/modified to ensure all components see updated list.
 * Usually called after optimisticallyAddTag() to validate and sync with server.
 */
export async function invalidateAndRefetchCustomerTags() {
  queryClient.removeQueries({ queryKey: ['customer-tags'] });
  await queryClient.ensureQueryData(customerTagsQueryOptions);
}

/**
 * Handles cache invalidation for customer tags based on rule operations.
 * Optimistically updates cache for immediate UI feedback, then refetches in background.
 * @param {Object} rule - The rule object containing operations
 */
export function handleCustomerTagsCacheInvalidation(rule) {
  const hasCustomTagOperation = rule.operations?.some(op => 
    op.key === 'add_crm_tags' || op.key === 'remove_crm_tags'
  );

  if (!hasCustomTagOperation) return;

  // Extract tag names from the rule operations
  rule.operations?.forEach(operation => {
    if (operation.key === 'add_crm_tags') {
      operation.input_fields_required?.forEach(input => {
        if (input.modelValue && input.identifier === 'tag_name') {
          // Optimistically add the tag to cache
          optimisticallyAddTag(input.modelValue);
        }
      });
    }
  });

  // Trigger full refetch in background for validation
  setTimeout(() => {
    invalidateAndRefetchCustomerTags();
  }, 100);
}
